import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { isNil } from 'lodash';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { IntercomHelp } from '../../../../../../../components/help/intercom-help';
import { FullScreenIonModal } from '../../../../../../../components/modal/full-screen-ion-modal';
import { IntercomArticle } from '../../../../../../../config/intercom';
import i18n from '../../../../../../../i18n';
import { FlowItem } from '../../../../../shared/model/flow.types';
import { FlowItemMultipass } from './flow-item-multipass';

export interface MultipassPathDetails {
  item: FlowItem;
  cb?: () => void;
}

interface MultipassPathProps {
  isOpen: boolean;
  multipassDetails: MultipassPathDetails | undefined;
  onClose: () => void;
}

const MultipassPathModal = (props: MultipassPathProps): ReactElement => {
  const { isOpen, multipassDetails, onClose } = props;

  const startItem = useMemo(() => {
    return multipassDetails?.item;
  }, [multipassDetails?.item]);

  const onMultipassConverted = useCallback(() => {
    onClose();
  }, [onClose]);

  const modalRef = useRef<HTMLIonModalElement>(null);

  return (
    <>
      <FullScreenIonModal
        ref={modalRef}
        isOpen={isOpen}
        isFullScreen={true} // force full screen
        onDidDismiss={() => {
          onClose();
        }}
      >
        <>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot={'start'}>
                <IonButton
                  onClick={() => {
                    modalRef.current?.dismiss();
                  }}
                >
                  <IonIcon icon={close} size="large" />
                </IonButton>
              </IonButtons>
              <IonTitle>{i18n.t('welding:multipass_settings')}</IonTitle>
              <div slot="end" style={{ display: 'flex' }}>
                <IntercomHelp articleId={IntercomArticle.MULTIPASS_WELD} />
              </div>
            </IonToolbar>
          </IonHeader>
          {startItem && (
            <FlowItemMultipass
              startItem={startItem}
              onMultipassConverted={onMultipassConverted}
              presentingEl={modalRef.current ?? undefined}
              forceFullScreenModals={true} // force full screen
            />
          )}
        </>
      </FullScreenIonModal>
    </>
  );
};

interface UseMultipassPathResult {
  multipassPathComponent: ReactElement;
  openMultipassPath: (item: FlowItem, cb?: () => void) => void;
  closeMultipassPath: () => void;
}

const useMultipassPath = (): UseMultipassPathResult => {
  const [mulitipassPathDetails, setMultipassPathDetails] = useState<
    MultipassPathDetails | undefined
  >();

  const openMultipassPath = useCallback((item: FlowItem, cb?: () => void) => {
    setMultipassPathDetails({ item, cb });
  }, []);

  const closeMultipassPath = useCallback(() => {
    if (mulitipassPathDetails?.cb) {
      mulitipassPathDetails.cb();
    }
    setMultipassPathDetails(undefined);
  }, [mulitipassPathDetails]);

  const multipassPathComponent = useMemo(() => {
    return (
      <MultipassPathModal
        isOpen={!isNil(mulitipassPathDetails)}
        multipassDetails={mulitipassPathDetails}
        onClose={closeMultipassPath}
      />
    );
  }, [closeMultipassPath, mulitipassPathDetails]);

  const result = useMemo(() => {
    return {
      multipassPathComponent,
      openMultipassPath,
      closeMultipassPath,
    };
  }, [closeMultipassPath, multipassPathComponent, openMultipassPath]);

  return result;
};

export { useMultipassPath };
